import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { downloadCsv, query } from '../api/client'
import { useInfinite, useResource, useToast } from '../api/hooks'
import type { AccessEvent, AuditEvent, CountResult, DoorListItem } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { PageHeader } from '../components/Layout'
import {
  Alert, ErrorBox, InfiniteFooter, Toast, describeError, formatDateTime,
} from '../components/Ui'
import { de, fmt } from '../i18n/de'

export function LogPage() {
  const { me } = useAuth()
  const [params] = useSearchParams()
  const { toast, show, clear } = useToast()

  const [day, setDay] = useState(() => new Date().toISOString().slice(0, 10))
  const [doorID, setDoorID] = useState('')
  const [result, setResult] = useState('')
  const [search, setSearch] = useState('')

  const doorCodeId = params.get('doorCodeId') ?? ''
  const doors = useResource<DoorListItem[]>('/doors')

  // Bei einem Türcodefilter aus dem Detail wird der Tag nicht eingeschränkt, sonst sieht man
  // den Verlauf nur für heute.
  const filter = query({
    from: doorCodeId ? '' : `${day}T00:00:00`,
    to: doorCodeId ? '' : `${day}T23:59:59`,
    doorId: doorID,
    doorCodeId,
    result,
    q: search,
  })

  const events = useInfinite<AccessEvent>(`/accessevents${filter}`)
  const count = useResource<CountResult>(`/accessevents/count${filter}`)

  const exportCsv = async () => {
    try {
      await downloadCsv(`/accessevents/export${filter}`, `zutritte-${day}.csv`)
    } catch (error) {
      show(describeError(error), 'warn')
    }
  }

  const restricted = me?.groupScope === 'restricted'

  return (
    <>
      <PageHeader
        title={de.log.title}
        subtitle={me?.timezone ? fmt(de.log.timesIn, { timezone: me.timezone }) : undefined}
        actions={me?.permissions.includes('log.export')
          ? <button className="btn btn-ghost" onClick={() => void exportCsv()}>⭳ {de.log.export}</button>
          : undefined} />

      <div className="toolbar">
        {!doorCodeId && (
          <input type="date" value={day} className="mono" style={{ width: 150 }}
                 onChange={event => setDay(event.target.value)} />
        )}

        <select value={doorID} onChange={event => setDoorID(event.target.value)} style={{ width: 'auto' }}>
          <option value="">{de.log.columnDoor}: {de.app.all}</option>
          {(doors.data ?? []).map(door => (
            <option key={door.id} value={door.id}>{door.name}</option>
          ))}
        </select>

        <select value={result} onChange={event => setResult(event.target.value)} style={{ width: 'auto' }}>
          <option value="">{de.log.filterResult}: {de.app.all}</option>
          <option value="granted">{de.log.resultGranted}</option>
          <option value="denied">{de.log.resultDenied}</option>
        </select>

        <input type="text" placeholder={`🔍 ${de.doorcodes.searchPlaceholder}`} value={search}
               onChange={event => setSearch(event.target.value)} style={{ width: 200 }} />

        <div className="spacer" />
        <span className="sm muted">
          {count.data ? fmt(de.doorcodes.hits, { count: count.data.count }) : ''}
        </span>
      </div>

      {restricted && (
        <div style={{ marginBottom: 'var(--sp-3)' }}>
          <Alert kind="info">
            {de.log.restrictedHint} {de.log.restrictedUnknownHint}
          </Alert>
        </div>
      )}

      {events.error && <ErrorBox error={events.error} onRetry={events.reload} />}

      <div className="card">
        <table>
          <thead>
            <tr>
              <th style={{ width: 170 }}>{de.log.columnTime}</th>
              <th>{de.log.columnDoor}</th>
              <th>{de.log.columnDoorCode}</th>
              <th style={{ width: 60 }}>{de.log.columnMethod}</th>
              <th>{de.log.columnResult}</th>
            </tr>
          </thead>
          <tbody>
            {events.items.map(event => (
              <>
                <tr key={event.id}>
                  <td className="mono faint">
                    {formatDateTime(event.occurredAt)}
                    {event.reportedLate && (
                      <span className="xs status status-warn" title={de.log.reportedLateHint}>
                        {' '}· {de.log.reportedLate}
                      </span>
                    )}
                  </td>
                  <td>{event.doorName}</td>
                  <td>
                    {event.doorCodeName ?? <span className="faint">{de.app.none}</span>}
                  </td>
                  <td className="muted">
                    {event.method === 'keytag' ? de.log.methodKeyTag : de.log.methodCode}
                  </td>
                  <td>
                    {event.granted
                      ? <span className="status status-ok">✓ {de.log.granted}</span>
                      : <span className="status" style={{ color: 'var(--danger)' }}>
                          ✗ {de.log.denied}
                        </span>}
                  </td>
                </tr>
                {/* Begründung im Klartext — ohne sie ist jede Abweisung ein Support-Ticket. */}
                {!event.granted && event.explanation && (
                  <tr key={`${event.id}-why`} className="note-row">
                    <td />
                    <td colSpan={4}>
                      <div className="inner" style={{ color: 'var(--text-muted)' }}>
                        {event.explanation}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}

            {!events.loading && events.items.length === 0 && (
              <tr><td colSpan={5} className="faint sm">{de.app.noEntries}</td></tr>
            )}
          </tbody>
        </table>

        <InfiniteFooter hasMore={events.hasMore} loading={events.loading}
                        onLoadMore={events.loadMore} />
      </div>

      <div style={{ marginTop: 'var(--sp-4)' }}>
        <Alert kind="info">{de.log.retention}</Alert>
      </div>

      {toast && <Toast message={toast.message} kind={toast.kind} onDone={clear} />}
    </>
  )
}

export function AuditPage() {
  const events = useInfinite<AuditEvent>('/auditevents')

  return (
    <>
      <PageHeader title={de.audit.title} subtitle={de.audit.intro} />

      {events.error && <ErrorBox error={events.error} onRetry={events.reload} />}

      <div className="card">
        <table>
          <thead>
            <tr>
              <th style={{ width: 170 }}>{de.audit.columnTime}</th>
              <th style={{ width: 150 }}>{de.audit.columnActor}</th>
              <th>{de.audit.columnObject}</th>
              <th>{de.audit.columnAction}</th>
            </tr>
          </thead>
          <tbody>
            {events.items.map(event => (
              <>
                <tr key={event.id}>
                  <td className="mono faint">{formatDateTime(event.occurredAt)}</td>
                  <td>
                    {/* Eingriffe im Superadmin-Kontext farblich gekennzeichnet und für
                        Admins der Organisation sichtbar. Das ist beabsichtigt. */}
                    <span style={event.actorRole === 'superadmin'
                      ? { color: 'var(--superadmin)' } : undefined}>
                      {event.actorName || event.actorRole}
                    </span>
                  </td>
                  <td>{describeObject(event)}</td>
                  <td>{describeAction(event.action)}</td>
                </tr>
                {event.detail && (
                  <tr key={`${event.id}-detail`} className="note-row">
                    <td /><td />
                    <td colSpan={2}>
                      <div className="inner" style={{ color: 'var(--text-muted)' }}>{event.detail}</div>
                    </td>
                  </tr>
                )}
              </>
            ))}

            {!events.loading && events.items.length === 0 && (
              <tr><td colSpan={4} className="faint sm">{de.app.noEntries}</td></tr>
            )}
          </tbody>
        </table>

        <InfiniteFooter hasMore={events.hasMore} loading={events.loading}
                        onLoadMore={events.loadMore} />
      </div>
    </>
  )
}

function describeObject(event: AuditEvent): string {
  const type = {
    doorcode: de.doorcodes.title,
    group: 'Gruppe',
    door: 'Tür',
    account: 'Benutzer',
    organization: de.nav.organization,
  }[event.objectType] ?? event.objectType

  return `${type} ${event.objectName}`.trim()
}

function describeAction(action: string): string {
  switch (action) {
    case 'created': return 'angelegt'
    case 'updated': return 'geändert'
    case 'deleted': return 'gelöscht'
    case 'activated': return 'aktiviert'
    case 'deactivated': return 'deaktiviert'
    case 'schedule_changed': return 'Zeitplan geändert'
    case 'doors_changed': return 'Türen geändert'
    case 'groups_changed': return 'Gruppen geändert'
    case 'code_regenerated': return 'Code neu erzeugt'
    case 'password_reset': return 'Passwort zurückgesetzt'
    case 'keytag_assigned': return 'Schlüsseltag zugewiesen'
    case 'keytag_removed': return 'Schlüsseltag entfernt'
    case 'codekey_replaced': return 'Türschlüssel ersetzt'
    default: return action
  }
}
